import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import api from 'app/modules/Remote/api';
import {ApiFeature} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization} from 'app/modules/Remote/Organization';
import {ApiProject} from 'app/modules/Remote/Project';
import {useAuth, useUserInfo} from 'app/providers/AuthProvider';
import {useProjects} from 'app/providers/ProjectsProvider';
import * as hookUtils from 'app/utils/hookUtils';
import {getLayerKeyFromSourceAndLayer} from 'app/utils/layerUtils';
import * as mapUtils from 'app/utils/mapUtils';
import * as noteUtils from 'app/utils/noteUtils';
import {makeNoteUrlFragment, makeProjectDashboardUrl} from 'app/utils/routeUtils';

import {ObservationMap} from './ObservationMap';
import * as cs from './styles.styl';
import {AgentResponse} from './utils';

interface NotePreviewProps {
  featureCollectionId: number;
  feature: I.ImmutableOf<ApiFeature>;
  agentResponse: AgentResponse[];
  title: React.ReactElement;
}
interface NoteModification {
  isDismissed?: boolean;
  isSaved?: boolean;
  noteUrl?: string;
}
export interface AgentNoteImage {
  layer_id: string;
  source_id: string;
  sensing_time: string;
}

const DUMMY_IMAGE_REFS: mapUtils.MapImageRefs = [{layerKey: 'none', cursor: null}];

export const NotesPreview = ({
  featureCollectionId,
  feature,
  agentResponse,
  title,
}: NotePreviewProps) => {
  const [projects, featureCollectionsById, , _projectsMeta] = useProjects();

  const project: I.ImmutableOf<ApiProject> | null = React.useMemo(() => {
    if (!projects) return [null, null];

    const projectEntry = projects.findEntry((project) =>
      project!.get('featureCollections').some((pfc) => pfc!.get('id') === featureCollectionId)
    );

    return projectEntry ? projectEntry[1] : null;
  }, [projects, featureCollectionId]);

  const featureCollection = React.useMemo(
    () => featureCollectionsById.get(featureCollectionId),
    [featureCollectionsById, featureCollectionId]
  );

  const [organization] = useUserInfo();
  const auth = useAuth();

  const [parsedOutputNoteObjects, setParsedOutputNoteObjects] = React.useState<AgentResponse[]>([]);

  const [currentNoteIndex, setCurrentNoteIndex] = React.useState(0);
  const [currentNote, setCurrentNote] = React.useState<(AgentResponse & NoteModification) | null>(
    null
  );

  const parsedImageRefs = hookUtils.useContinuity(
    React.useMemo(() => {
      if (!currentNote || !currentNote.before_image || !currentNote.after_image) {
        return null;
      }
      return parseSelectedImages(currentNote.before_image, currentNote.after_image);
    }, [currentNote])
  );

  //TODO(eva): right now we're just saving whether a note observation is dismissed/saved in state,
  //but once we are storing info about runs, we will want to store this in the run
  const [noteModifications, setNoteModifications] = React.useState<
    Record<number, NoteModification>
  >({});

  const getNoteWithModifications = React.useCallback(
    (index: number) => {
      const baseNote = parsedOutputNoteObjects[index];
      if (!baseNote) return null;

      const modifications = noteModifications[index] || {};
      return {
        ...baseNote,
        ...modifications,
      };
    },
    [parsedOutputNoteObjects, noteModifications]
  );

  const updateNoteModification = (
    index: number,
    modification: {isDismissed?: boolean; isSaved?: boolean; noteUrl?: string}
  ) => {
    setNoteModifications((prev) => ({
      ...prev,
      [index]: {...prev[index], ...modification},
    }));
  };

  React.useEffect(() => {
    const parsedOutput = agentResponse ? parseOutput(agentResponse) : [];
    setParsedOutputNoteObjects(parsedOutput);
    setNoteModifications({});
  }, [agentResponse]);

  React.useEffect(() => {
    setCurrentNote(getNoteWithModifications(currentNoteIndex));
  }, [currentNoteIndex, getNoteWithModifications, noteModifications, parsedOutputNoteObjects]);

  const handlePrev = () => {
    const newIndex = Math.max(0, currentNoteIndex - 1);
    setCurrentNoteIndex(newIndex);
  };

  const handleNext = () => {
    const newIndex = Math.min(parsedOutputNoteObjects.length - 1, currentNoteIndex + 1);
    setCurrentNoteIndex(newIndex);
  };

  const notesAreReady =
    feature && agentResponse && featureCollection && project && parsedOutputNoteObjects.length > 0;

  return (
    <>
      <B.Section
        collapsible={false}
        title={title}
        className={notesAreReady ? cs.activeLensAgent : ''}
      >
        {notesAreReady && (
          <>
            <div className={cs.notesPreviewContainer}>
              {currentNote &&
                currentNote.before_image &&
                currentNote.after_image &&
                parsedImageRefs &&
                project && (
                  <React.Fragment>
                    <NotePreview
                      note={currentNote}
                      handleNext={handleNext}
                      handlePrev={handlePrev}
                      parsedImageRefs={parsedImageRefs}
                      projectId={project.get('id')}
                      feature={feature}
                      featureCollection={featureCollection}
                      project={project}
                      organization={organization}
                      updateNoteModification={(modification) =>
                        updateNoteModification(currentNoteIndex, modification)
                      }
                      firebaseToken={auth.status === 'logged in' ? auth.firebaseToken : ''}
                      notesLength={parsedOutputNoteObjects.length}
                      currentNoteIndex={currentNoteIndex}
                    />
                  </React.Fragment>
                )}
            </div>
          </>
        )}
      </B.Section>
    </>
  );
};

export default NotesPreview;

const NotePreview = ({
  note,
  parsedImageRefs,
  projectId,
  feature,
  featureCollection,
  project,
  organization,
  updateNoteModification,
  firebaseToken,
  handleNext,
  handlePrev,
  notesLength,
  currentNoteIndex,
}: {
  note: AgentResponse & NoteModification;
  parsedImageRefs: mapUtils.MapImageRefs;
  projectId: string;
  feature: I.ImmutableOf<ApiFeature>;
  featureCollection: I.ImmutableOf<ApiFeatureCollection>;
  project: I.ImmutableOf<ApiProject>;
  organization: I.ImmutableOf<ApiOrganization>;
  updateNoteModification: (modification: NoteModification) => void;
  firebaseToken: string;
  handleNext: () => void;
  handlePrev: () => void;
  notesLength: number;
  currentNoteIndex: number;
}) => {
  const isDismissed = !!note.isDismissed;
  const isSaved = !!note.isSaved;
  const [isSaving, setIsSaving] = React.useState(false);

  return (
    <div className={cs.notePreviewCard}>
      {note.geometry && (
        <ObservationMap
          selectedFeature={feature}
          selectedFeatureCollection={featureCollection}
          organization={organization}
          imageRefs={parsedImageRefs}
          selectedFeatureIdParams={I.Set([feature.get('id').toString()])}
          refreshFeatureCollection={() => {}}
          selectedProject={project}
          noteGeometry={note.geometry}
          firebaseToken={firebaseToken}
        />
      )}
      <div style={{position: 'absolute', bottom: '4rem', right: '2rem'}}>
        <div className={cs.notePreviewContent}>
          {note.text}
          {note.noteUrl && (
            <a href={note.noteUrl} target="_blank" rel="noopener noreferrer">
              Link to note
            </a>
          )}
          <div className={cs.notePreviewActions}>
            <div className={cs.notePreviewControls}>
              <B.Button
                minimal
                onClick={handlePrev}
                icon="chevron-left"
                disabled={currentNoteIndex === 0}
              ></B.Button>
              <div className={cs.noteNumber}>
                <B.Icon icon="clean" />
                {` Note ${currentNoteIndex + 1} of ${notesLength}`}
              </div>
              <B.Button
                minimal
                onClick={handleNext}
                rightIcon="chevron-right"
                disabled={currentNoteIndex === notesLength - 1}
              ></B.Button>
            </div>
            <B.Button
              className={cs.agentAction}
              onClick={async () => {
                setIsSaving(true);
                const newNote = await createNote(
                  note,
                  parsedImageRefs ?? DUMMY_IMAGE_REFS,
                  projectId,
                  feature.get('id'),
                  featureCollection.get('id')
                );
                const noteLink = makeProjectDashboardUrl(
                  organization,
                  projectId,
                  'map',
                  [feature.get('id')],
                  makeNoteUrlFragment(newNote.id)
                );
                updateNoteModification({isSaved: true, noteUrl: noteLink ?? undefined});
                setIsSaving(false);
              }}
              intent="primary"
              disabled={isDismissed || isSaving || isSaved}
            >
              {isSaving ? 'Saving...' : isSaved ? 'Saved!' : 'Save as note'}
            </B.Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const createNote = async (
  note: AgentResponse,
  imageRefs: mapUtils.MapImageRefs,
  projectId: string,
  featureId: number,
  featureCollectionId: number
) => {
  //TODO(eva): we need to make sure this has createdBy for the lens agent
  const newNote = await api.featureCollections
    .features(featureCollectionId)
    .createNote(projectId, featureId, {
      text: note.label,
      attachments: [],
      geometry: note.geometry ?? null,
      graph: null,
      tagIds: {},
      properties: {},
      userId: 'lens-agent@upstream.tech',
      ...noteUtils.notePropertiesFromImageRefs(imageRefs),
    })
    .then((note) => {
      return note.toJS().data;
    });
  return newNote;
};

const parseOutput = (agentLines: AgentResponse[]): AgentResponse[] => {
  if (!agentLines) {
    return [];
  }

  const output = agentLines.filter((line) => line.type === 'note' && line.text); // Only include notes with text
  return output;
};

const parseSelectedImages = (
  beforeImage: AgentNoteImage,
  afterImage: AgentNoteImage
): mapUtils.MapImageRefs | null => {
  return [
    {
      layerKey: getLayerKeyFromSourceAndLayer(beforeImage.source_id, beforeImage.layer_id),
      cursor: beforeImage.sensing_time,
    },
    {
      layerKey: getLayerKeyFromSourceAndLayer(afterImage.source_id, afterImage.layer_id),
      cursor: afterImage.sensing_time,
    },
  ];
};
